/**
 * Extract filename from R2 presigned URL
 * @param {string} url - The presigned URL
 * @param {string} defaultFileName - Fallback filename if extraction fails
 * @returns {string} - The extracted or default filename
 */
export const extractFilenameFromUrl = (url, defaultFileName = 'document') => {
  if (!url) return defaultFileName;

  try {
    // Try to get filename from URL path
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const fileNameFromUrl = pathParts[pathParts.length - 1];
    
    // If we found a filename with extension in URL, use it
    if (fileNameFromUrl && fileNameFromUrl.includes('.')) {
      // Decode and clean the filename
      let fileName = decodeURIComponent(fileNameFromUrl.split('?')[0]);
      
      // Remove timestamp prefix if present (e.g., "1234567890_file.png" -> "file.png")
      const parts = fileName.split('_');
      if (parts.length > 1 && !isNaN(parts[0])) {
        fileName = parts.slice(1).join('_');
      }
      
      // Also handle dash-separated timestamps (e.g., "1234567890-file.png" -> "file.png")
      const dashParts = fileName.split('-');
      if (dashParts.length > 1 && !isNaN(dashParts[0])) {
        fileName = dashParts.slice(1).join('-');
      }
      
      return fileName;
    }
  } catch (e) {
    // If URL parsing fails, use default filename
    console.log('Could not parse filename from URL, using default');
  }

  return defaultFileName;
};

/**
 * Download file from URL with proper filename
 * @param {string} url - The file URL
 * @param {string} defaultFileName - Fallback filename if extraction fails
 */
export const downloadFileFromUrl = async (url, defaultFileName = 'document') => {
  if (!url) {
    throw new Error('URL is required');
  }

  const fileName = extractFilenameFromUrl(url, defaultFileName);
  
  const response = await fetch(url);
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};
